import { RefObject, useLayoutEffect, useRef, useState } from 'react';

interface UseScrollEndGateArgs {
  onReachedEnd: () => void;
}

export interface ScrollEndGate {
  scrollRef: RefObject<HTMLDivElement>;
  hasReachedEnd: boolean;
  onScroll: () => void;
}

const SCROLL_END_THRESHOLD_PX = 8;

const isScrolledToEnd = (element: HTMLDivElement): boolean => {
  return (
    element.scrollTop + element.clientHeight >=
    element.scrollHeight - SCROLL_END_THRESHOLD_PX
  );
};

export const useScrollEndGate = ({
  onReachedEnd,
}: UseScrollEndGateArgs): ScrollEndGate => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasFiredRef = useRef(false);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);

  const markReachedEnd = (): void => {
    if (hasFiredRef.current) {
      return;
    }

    hasFiredRef.current = true;
    setHasReachedEnd(true);
    onReachedEnd();
  };

  const onScroll = (): void => {
    const element = scrollRef.current;

    if (!element) {
      return;
    }

    if (isScrolledToEnd(element)) {
      markReachedEnd();
    }
  };

  useLayoutEffect(() => {
    const unlockWhenContentDoesNotOverflow = (): void => {
      const element = scrollRef.current;

      if (!element) {
        return;
      }

      if (isScrolledToEnd(element)) {
        markReachedEnd();
      }
    };

    unlockWhenContentDoesNotOverflow();
  }, []);

  return { scrollRef, hasReachedEnd, onScroll };
};